import { InvitationEmailField } from "./EhpadInvitationParts";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { API } from "../../../services/api";
import { FieldLabel } from "../AdminCommon";
import { nextEhpadColor } from "../adminConstants";
import { ProductionSubcategoryManager } from "./ProductionSubcategoryManager";
import {
    INVALID_INVITATION_EMAIL,
    invitationEmailUnavailableMessage,
    invitationErrorMessage,
    isValidInvitationEmail,
    sendEhpadInvitation,
} from "./ehpadInvitations";
import { CONTROLE_MODE_OPTIONS, emptyEhpadForm } from "./ehpadRules";
import { btnBlackStyle, errorStyle, fieldStyle, inputStyle } from "../styles/adminDashboardStyles";

export function EhpadCreateForm({ ehpads, tournees, onCreated, onInvited }) {
    const [form, setForm] = useState(() => emptyEhpadForm(nextEhpadColor(ehpads)));
    const [colorTouched, setColorTouched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);

    // La couleur proposée suit la liste chargée, tant que l'utilisateur ne l'a pas choisie lui-même.
    useEffect(() => {
        if (!colorTouched) setForm(f => ({ ...f, color: nextEhpadColor(ehpads) }));
    }, [ehpads, colorTouched]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError(null);
        const inviteEmail = form.inviteEmail.trim();
        if (inviteEmail && !isValidInvitationEmail(inviteEmail)) {
            setFormError(INVALID_INVITATION_EMAIL);
            return;
        }
        setSubmitting(true);
        if (inviteEmail) {
            // Email déjà pris (compte portail ou invitation en cours ailleurs) : l'EHPAD n'est pas créé.
            const taken = await invitationEmailUnavailableMessage(inviteEmail, null);
            if (taken) {
                setFormError(taken);
                setSubmitting(false);
                return;
            }
        }
        let data;
        try {
            ({ data } = await API.post("/admin/ehpads", {
                code: form.code,
                nom: form.nom,
                address: form.address || null,
                phone: form.phone || null,
                // L'email de raccordement devient le premier email de contact de l'EHPAD.
                emailsContact: inviteEmail ? [inviteEmail] : [],
                emailsFax: [],
                tourneeId: form.tourneeId ? Number(form.tourneeId) : null,
                color: form.color,
                controleMode: form.controleMode || null,
                productionSubcategories: form.productionSubcategories,
            }));
            onCreated(data);
            const nextEhpads = [...ehpads, data];
            setForm(emptyEhpadForm(nextEhpadColor(nextEhpads)));
            setColorTouched(false);
        } catch (err) {
            setFormError(err.response?.data?.message ?? "Erreur lors de la création.");
            setSubmitting(false);
            return;
        }
        if (inviteEmail) {
            try {
                onInvited(await sendEhpadInvitation(inviteEmail, data.id));
            } catch (err) {
                setFormError(invitationErrorMessage(err, "EHPAD créé"));
            }
        }
        setSubmitting(false);
    };

    return (
        <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 150px", gap: "16px", marginBottom: "16px" }}>
                <div style={fieldStyle}>
                    <FieldLabel required>Code</FieldLabel>
                    <input id="admin-ehpad-code" style={inputStyle} placeholder="4S" value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} required />
                </div>
                <div style={fieldStyle}>
                    <FieldLabel required>Nom</FieldLabel>
                    <input id="admin-ehpad-name" style={inputStyle} placeholder="Résidence Les Acacias" value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} required />
                </div>
                <div style={fieldStyle}>
                    <FieldLabel>Téléphone</FieldLabel>
                    <input id="admin-ehpad-phone" style={inputStyle} placeholder="01 45 …" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                </div>
            </div>
            <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 260px" }}>
                    <FieldLabel>Adresse</FieldLabel>
                    <input id="admin-ehpad-address" style={inputStyle} placeholder="12 rue des Lilas, 94120…" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                </div>
                <div style={{ width: "200px" }}>
                    <FieldLabel>Tournée</FieldLabel>
                    <select id="admin-ehpad-tournee" style={inputStyle} value={form.tourneeId} onChange={e => setForm(f => ({ ...f, tourneeId: e.target.value }))}>
                        <option value="">— Aucune —</option>
                        {tournees.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                </div>
                <div style={{ width: "210px" }}>
                    <FieldLabel>Mode de contrôle</FieldLabel>
                    <select id="admin-ehpad-controle-mode" style={inputStyle} value={form.controleMode} onChange={e => setForm(f => ({ ...f, controleMode: e.target.value }))}>
                        {CONTROLE_MODE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                </div>
                <div style={{ width: "90px" }}>
                    <FieldLabel>Couleur</FieldLabel>
                    <input
                        id="admin-ehpad-color"
                        aria-label="Couleur du nouvel EHPAD"
                        type="color"
                        value={form.color}
                        onChange={e => { setColorTouched(true); setForm(f => ({ ...f, color: e.target.value.toUpperCase() })); }}
                        style={{ ...inputStyle, height: "38px", padding: "3px", cursor: "pointer" }}
                    />
                </div>
            </div>
            <div style={{ marginTop: "16px" }}>
                <FieldLabel>Sous-catégorie (planning, production, etc.) (facultatif)</FieldLabel>
                <ProductionSubcategoryManager
                    inputId="admin-ehpad-production-subcategory"
                    values={form.productionSubcategories}
                    draft={form.productionSubcategoryDraft}
                    onDraftChange={value => setForm(f => ({ ...f, productionSubcategoryDraft: value }))}
                    onValuesChange={values => setForm(f => ({ ...f, productionSubcategories: values }))}
                />
            </div>
            <div style={{ marginTop: "16px" }}>
                <InvitationEmailField
                    inputId="admin-ehpad-invite-email"
                    submitLabel="Créer l'EHPAD"
                    value={form.inviteEmail}
                    onChange={value => setForm(f => ({ ...f, inviteEmail: value }))}
                />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "16px" }}>
                <button type="submit" disabled={submitting} style={btnBlackStyle}>
                    {submitting ? "Création..." : "Créer l'EHPAD"}
                </button>
            </div>
            {formError && <p style={errorStyle}>{formError}</p>}
        </form>
    );
}
EhpadCreateForm.propTypes = {
    ehpads: PropTypes.arrayOf(PropTypes.object).isRequired,
    tournees: PropTypes.arrayOf(PropTypes.object).isRequired,
    onCreated: PropTypes.func.isRequired,
    onInvited: PropTypes.func.isRequired,
};
